import React, { useEffect, useState } from "react";
import { api, fmtDay, whatsappLink } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, ArrowRightCircle, Building2, Sparkles, Eye, Pencil, Trash2, Download } from "lucide-react";
import { downloadEnquiry } from "@/lib/pdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STATUS_COLORS = { new: "#D4AF37", reviewed: "#3B82F6", quoted: "#A78BFA", confirmed: "#10B981", declined: "#EF4444" };
const FILTERS = ["all", "new", "reviewed", "quoted", "confirmed", "declined"];

export default function EnquiriesPage() {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const r = await api.get("/enquiries", { params: filter !== "all" ? { status: filter } : {} });
    setEnquiries(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (e, status) => {
    await api.patch(`/enquiries/${e.id}`, { status });
    toast.success(`Marked as ${status}`);
    load();
  };

  const convert = async (e) => {
    if (!window.confirm(`Convert enquiry ${e.ref_code} into an order?`)) return;
    try {
      const { data } = await api.post(`/enquiries/${e.id}/convert`);
      toast.success(`Order created (${data.tracking_code})`);
      navigate(`/orders/${data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert");
    }
  };

  const removeEnq = async (e) => {
    if (!window.confirm(`Delete enquiry ${e.ref_code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/enquiries/${e.id}`);
      toast.success("Enquiry deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const waMessage = (e) =>
    `Hi ${e.contact_person || e.name}, thank you for your ${e.type === "corporate" ? "quotation request" : "event enquiry"} (${e.ref_code}) with HNC Event Catering! We've reviewed your request for ${e.event_name || "your event"} on ${fmtDay(e.event_date)} and would love to discuss the details.`;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="enquiries-page">
      <div className="mb-8">
        <div className="label-eyebrow">Customer Requests</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Enquiries</h1>
        <p className="text-zinc-500 mt-1 text-sm">Event enquiries & corporate quotation requests submitted through the portal.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`enq-filter-${f}`}
            className={`px-3 py-1.5 text-xs rounded-sm border capitalize transition-colors ${filter === f ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {enquiries.length === 0 && (
          <div className="card-lux p-10 text-center text-zinc-600 text-sm">No enquiries {filter !== "all" ? `with status "${filter}"` : "yet"}. Customers can submit via "Plan My Event" in their portal.</div>
        )}
        {enquiries.map((e) => (
          <div key={e.id} className="card-lux p-6" data-testid={`enquiry-${e.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                {e.type === "corporate" ? <Building2 className="w-5 h-5 text-[#D4AF37] mt-1" /> : <Sparkles className="w-5 h-5 text-[#D4AF37] mt-1" />}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-xl">{e.type === "corporate" ? e.company_name : e.name}</span>
                    <span className="text-[10px] font-mono text-zinc-500">{e.ref_code}</span>
                  </div>
                  <div className="text-sm text-zinc-400 mt-0.5">
                    {e.event_name && <span>{e.event_name} · </span>}
                    {fmtDay(e.event_date)}{e.event_time ? ` · ${e.event_time}` : ""} · {e.pax} pax
                    {e.budget_per_pax ? ` · Budget RM${e.budget_per_pax}/pax` : ""}
                  </div>
                  {(e.theme || e.theme_colour) && <div className="text-xs text-zinc-500 mt-0.5">{e.theme ? `Theme: ${e.theme}` : ""}{e.theme && e.theme_colour ? " · " : ""}{e.theme_colour ? `Colour: ${e.theme_colour}` : ""}</div>}
                  {e.address && <div className="text-xs text-zinc-500 mt-0.5">{e.address}</div>}
                </div>
              </div>
              <span className="stage-chip capitalize" style={{ color: STATUS_COLORS[e.status], borderColor: STATUS_COLORS[e.status] + "55", background: STATUS_COLORS[e.status] + "10" }} data-testid={`enq-status-${e.id}`}>
                {e.status}
              </span>
            </div>

            {((e.packages || []).length > 0 || (e.items || []).length > 0) && (
              <div className="mt-4 pt-4 border-t border-[#1a1a1a] text-xs space-y-1">
                {(e.packages || []).length > 0 && (
                  <div><span className="text-zinc-500">Packages: </span><span className="text-[#D4AF37]">{e.packages.join(", ")}</span></div>
                )}
                {(e.items || []).length > 0 && (
                  <div className="text-zinc-400">
                    <span className="text-zinc-500">Dishes: </span>
                    {e.items.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
                  </div>
                )}
              </div>
            )}
            {(e.remarks || e.requirements) && (
              <div className="mt-2 text-xs text-zinc-400">※ {e.remarks || e.requirements}</div>
            )}

            <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-[#1a1a1a]">
              <Button onClick={() => setViewing(e)} variant="outline" data-testid={`enq-view-${e.id}`} className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-9 text-xs rounded-sm">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> View Details
              </Button>
              <Button onClick={() => setEditing(e)} variant="outline" data-testid={`enq-edit-${e.id}`} className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-9 text-xs rounded-sm">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button onClick={() => downloadEnquiry(e)} variant="outline" data-testid={`enq-download-${e.id}`} className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-9 text-xs rounded-sm">
                <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
              </Button>
              <Button onClick={() => removeEnq(e)} variant="outline" data-testid={`enq-delete-${e.id}`} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 text-xs rounded-sm">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              {e.phone && (
                <a href={whatsappLink(e.phone, waMessage(e))} target="_blank" rel="noreferrer" data-testid={`enq-wa-${e.id}`}
                   className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-emerald-500 hover:text-emerald-400 text-xs transition-colors">
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              )}
              <select value={e.status} onChange={(ev) => setStatus(e, ev.target.value)} data-testid={`enq-status-select-${e.id}`}
                className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-9 text-xs capitalize">
                {FILTERS.filter((f) => f !== "all").map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {!e.order_id ? (
                <Button onClick={() => convert(e)} data-testid={`enq-convert-${e.id}`} className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] h-9 text-xs rounded-sm ml-auto">
                  <ArrowRightCircle className="w-3.5 h-3.5 mr-1.5" /> Convert to Order
                </Button>
              ) : (
                <Button onClick={() => navigate(`/orders/${e.order_id}`)} variant="outline" className="border-[#2a2a2a] h-9 text-xs rounded-sm ml-auto" data-testid={`enq-view-order-${e.id}`}>
                  View Order →
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {viewing && (
        <Dialog open onOpenChange={() => setViewing(null)}>
          <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="enquiry-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {viewing.type === "corporate" ? viewing.company_name : (viewing.invoice_name || viewing.name)}
                <span className="text-xs font-mono text-[#D4AF37] ml-2">{viewing.ref_code}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <DRow k="Type" v={viewing.type === "corporate" ? "Corporate Quotation" : "Customer Order"} />
              <DRow k="Contact" v={viewing.contact_person || viewing.name} />
              <DRow k="Phone" v={viewing.phone} />
              <DRow k="Email" v={viewing.email || viewing.customer_email} />
              <DRow k="Category" v={viewing.event_category} />
              <DRow k="Event" v={viewing.event_name} />
              <DRow k="Date" v={`${fmtDay(viewing.event_date)}${viewing.event_time ? ` · ${viewing.event_time}` : ""}${viewing.event_end_time ? ` – ${viewing.event_end_time}` : ""}`} />
              <DRow k="Pax" v={viewing.pax} />
              <DRow k="Theme" v={viewing.theme} />
              <DRow k="Theme Colour" v={viewing.theme_colour} />
              <DRow k="Menu" v={viewing.menu_notes} />
              <DRow k="Budget/pax" v={viewing.budget_per_pax ? `RM${viewing.budget_per_pax}` : ""} />
              <DRow k="Billing" v={viewing.billing_address} />
              <DRow k="Delivery" v={viewing.delivery_address || viewing.address} />
              <DRow k="Services" v={(viewing.services || []).join(", ")} />
              {(viewing.package_selections || []).map((ps, i) => (
                <div key={i} className="border-t border-[#1a1a1a] pt-2">
                  <div className="text-[#D4AF37] text-sm mb-1">{ps.package_name}{ps.pricing_mode === "set" ? <span className="text-zinc-400 text-xs"> × {ps.quantity || 1} set(s)</span> : null}</div>
                  {(ps.choices || []).map((c, j) => (
                    <div key={j} className="flex gap-2 text-xs py-0.5">
                      <span className="text-zinc-500 w-28 shrink-0">{c.slot}</span>
                      <span className="text-zinc-200">{c.item_name}{c.variant ? <span className="text-[#D4AF37]"> — {c.variant}</span> : null}</span>
                    </div>
                  ))}
                </div>
              ))}
              {(viewing.items || []).length > 0 && (
                <DRow k="Dishes" v={viewing.items.map((i) => `${i.name} ×${i.quantity}`).join(" · ")} />
              )}
              <DRow k="Remarks" v={viewing.remarks || viewing.requirements} />
              <DRow k="Status" v={viewing.status} />
              <DRow k="Submitted" v={fmtDay(viewing.created_at)} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => downloadEnquiry(viewing)} variant="outline" data-testid="enq-dialog-download"
                className="flex-1 border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] h-9 text-xs rounded-sm">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
              </Button>
              {viewing.phone && (
                <a href={whatsappLink(viewing.phone, waMessage(viewing))} target="_blank" rel="noreferrer"
                   className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-emerald-500 hover:text-emerald-400 text-xs transition-colors">
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              )}
              {!viewing.order_id && (
                <Button onClick={() => { setViewing(null); convert(viewing); }} className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C86B] h-9 text-xs rounded-sm">
                  <ArrowRightCircle className="w-3.5 h-3.5 mr-1.5" /> Convert to Order
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      {editing && (
        <EditEnquiryDialog enq={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EditEnquiryDialog({ enq, onClose, onSaved }) {
  const isCorp = enq.type === "corporate";
  const [f, setF] = useState({
    name: enq.name || "", invoice_name: enq.invoice_name || "", company_name: enq.company_name || "",
    contact_person: enq.contact_person || "", phone: enq.phone || "", email: enq.email || "",
    event_category: enq.event_category || "", event_date: (enq.event_date || "").slice(0, 10),
    event_time: enq.event_time || "", event_end_time: enq.event_end_time || "",
    pax: enq.pax ?? "", address: enq.address || "", billing_address: enq.billing_address || "",
    delivery_address: enq.delivery_address || "", theme: enq.theme || "", theme_colour: enq.theme_colour || "",
    budget_per_pax: enq.budget_per_pax ?? "", menu_notes: enq.menu_notes || "", remarks: enq.remarks || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/enquiries/${enq.id}`, {
        ...f,
        pax: f.pax === "" ? undefined : Number(f.pax),
        budget_per_pax: f.budget_per_pax === "" ? undefined : Number(f.budget_per_pax),
      });
      toast.success("Enquiry updated");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  const cls = "bg-[#050505] border-[#2a2a2a] h-9 text-sm";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-xl max-h-[88vh] overflow-y-auto" data-testid="enquiry-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit Enquiry <span className="text-xs font-mono text-[#D4AF37] ml-2">{enq.ref_code}</span></DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {isCorp ? (
            <>
              <F label="Company Name"><Input value={f.company_name} onChange={set("company_name")} data-testid="ee-company" className={cls} /></F>
              <F label="PIC Name"><Input value={f.contact_person} onChange={set("contact_person")} data-testid="ee-contact" className={cls} /></F>
            </>
          ) : (
            <>
              <F label="Invoice Name"><Input value={f.invoice_name} onChange={set("invoice_name")} data-testid="ee-invoice" className={cls} /></F>
              <F label="Contact Person"><Input value={f.contact_person} onChange={set("contact_person")} data-testid="ee-contact" className={cls} /></F>
            </>
          )}
          <F label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="ee-phone" className={cls} /></F>
          <F label="Email"><Input type="email" value={f.email} onChange={set("email")} data-testid="ee-email" className={cls} /></F>
          <F label="Event Date"><Input type="date" value={f.event_date} onChange={set("event_date")} data-testid="ee-date" className={cls} /></F>
          <F label={isCorp ? "Time (start / end)" : "Event Time"}>
            <div className="flex items-center gap-1.5">
              <Input type="time" value={f.event_time} onChange={set("event_time")} data-testid="ee-time" className={cls} />
              {isCorp && <Input type="time" value={f.event_end_time} onChange={set("event_end_time")} data-testid="ee-time-end" className={cls} />}
            </div>
          </F>
          <F label="Pax"><Input type="number" min="0" value={f.pax} onChange={set("pax")} data-testid="ee-pax" className={cls} /></F>
          {!isCorp && <F label="Event Category"><Input value={f.event_category} onChange={set("event_category")} className={cls} /></F>}
          <F label="Theme Colour"><Input value={f.theme_colour} onChange={set("theme_colour")} data-testid="ee-theme-colour" className={cls} /></F>
          {isCorp ? (
            <>
              <F label="Budget / Pax (RM)"><Input type="number" min="0" step="0.01" value={f.budget_per_pax} onChange={set("budget_per_pax")} data-testid="ee-budget" className={cls} /></F>
              <F label="Event Address" wide><Textarea value={f.address} onChange={set("address")} data-testid="ee-address" className="bg-[#050505] border-[#2a2a2a] text-sm" /></F>
              <F label="Menu" wide><Textarea value={f.menu_notes} onChange={set("menu_notes")} data-testid="ee-menu" className="bg-[#050505] border-[#2a2a2a] text-sm" /></F>
              <F label="Theme"><Input value={f.theme} onChange={set("theme")} data-testid="ee-theme" className={cls} /></F>
            </>
          ) : (
            <>
              <F label="Billing Address" wide><Textarea value={f.billing_address} onChange={set("billing_address")} data-testid="ee-billing" className="bg-[#050505] border-[#2a2a2a] text-sm" /></F>
              <F label="Delivery Address" wide><Textarea value={f.delivery_address} onChange={set("delivery_address")} data-testid="ee-delivery" className="bg-[#050505] border-[#2a2a2a] text-sm" /></F>
              <F label="Remarks" wide><Textarea value={f.remarks} onChange={set("remarks")} data-testid="ee-remarks" className="bg-[#050505] border-[#2a2a2a] text-sm" /></F>
            </>
          )}
        </div>
        <p className="text-[10px] text-zinc-500">Package & dish selections can't be edited here — ask the customer to resubmit, or adjust details after converting to an order.</p>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="ee-save" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">{saving ? "Saving…" : "Save Changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children, wide }) {
  return (
    <div className={`space-y-1 ${wide ? "col-span-2" : ""}`}>
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}

function DRow({ k, v }) {
  if (!v && v !== 0) return null;
  return <div className="flex gap-2"><span className="text-zinc-500 text-xs w-28 shrink-0 pt-0.5">{k}</span><span className="text-zinc-200 flex-1 capitalize-first">{v}</span></div>;
}
